/**
 * Mobile (below `lg`) view of the volume-price tiers: a full-screen slide-out (`SliderMenu`
 * from `MoreYouBuyMoreYouSave.styled.ts`) with back and close controls and
 * `MoreYouBuyMoreYouSaveRows` in its light variant. It ignores `trigger` and
 * `hasDiscountPrices`: the row opens it by setting `moreYouBuyExpanded`.
 *
 * Side effects on the page: opening scrolls the window to the top and, after 100 ms, sets the
 * `<body>` style to `overflow: hidden`; closing restores the body style saved at opening. On
 * mount it sets `moreYouBuyExpanded` to false.
 */
import React, { createRef, RefObject, useEffect, useState } from "react";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useWindowSize } from "@ryerson/frontend.common";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { SliderMenu } from "./MoreYouBuyMoreYouSave.styled";
import { Icon } from "@ryerson/frontend.assets";
import rgba from "polished/lib/color/rgba";
import { MoreYouBuyMoreYouSaveChildProps } from "./MoreYouBuyMoreYouSave";
import { useApplication } from "@ryerson/frontend.application";
import MoreYouBuyMoreYouSaveRows from "./MoreYouBuyMoreYouSaveRows";

const MoreYouBuyHeader = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

/** Mobile tier slide-out; rendered by `MoreYouBuyMoreYouSave.tsx` below the `lg` breakpoint. */
const MoreYouBuyMoreYouSaveMobile: React.FC<MoreYouBuyMoreYouSaveChildProps> = ({
	trigger,
	offset,
	moreYouBuyExpanded,
	setMoreYouBuyExpanded,
	content,
	productDetail,
	quantityUom,
	priceUom,
	filteredDiscountedPrice,
	hasDiscountPrices,
	handleOnToggle,
	rowIndex,
}) => {
	const { theme } = useTheme();

	const { white, gray, secondaryBrand } = theme.colors.primary;

	const [hasMounted, setHasMounted] = useState<boolean>(false);
	const [domNodeRefs] = useState<any>({});
	const windowSize = useWindowSize();
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const { isNative } = useApplication();

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};
	const onToggle = (toggle: boolean) => {
		setMoreYouBuyExpanded && setMoreYouBuyExpanded(toggle);
		handleOnToggle && handleOnToggle(rowIndex || 0);
	};
	// Skipped on the mount run (`hasMounted` is still false); afterwards each change of
	// `moreYouBuyExpanded` flips the slide-open / slide-close classes.
	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["more-you-buy-menu"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");

			if (moreYouBuyExpanded) {
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 100);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [moreYouBuyExpanded]);

	useEffect(() => {
		setHasMounted(true);
		setMoreYouBuyExpanded(false);
	}, []);

	return (
		<SliderMenu
			{...{ theme }}
			ref={creatDomNodeRef(`more-you-buy-menu`)}
			className="more-you-buy-menu slide-close"
			css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
		>
			<Flex css={{ height: windowSize[1] - 100, overflowY: "scroll" }} direction="column">
				<MoreYouBuyHeader theme={theme}>
					<Flex alignItems="center">
						<FlexItem grow={1}>
							<Flex
								justifyContent="flex-start"
								onClick={() =>
									setMoreYouBuyExpanded && setMoreYouBuyExpanded(false)
								}
							>
								<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
									<Icon
										icon="chevron-left"
										size="md"
										onClick={() => onToggle(false)}
										color={white}
										circled={true}
										css={{ borderColor: gray }}
									/>
								</FlexItem>
								<FlexItem
									css={css`
										padding-left: 10px;
									`}
								>
									<Typography
										variant="p"
										color={theme.colors.tertiary.label}
										size="md"
									>
										{content.back}
									</Typography>
								</FlexItem>
							</Flex>
						</FlexItem>
						<FlexItem>
							<Flex
								css={{
									height: "50px",
									width: "50px",
									borderLeft: `1px solid ${rgba(white, 0.1)}`,
								}}
								justifyContent="center"
								alignItems="center"
							>
								<Icon
									icon="close"
									size="sm"
									onClick={() => onToggle(false)}
									color={theme.colors.primary.white}
								/>
							</Flex>
						</FlexItem>
					</Flex>
				</MoreYouBuyHeader>
				<div css={{ padding: "20px 0px" }}>
					<Typography color={secondaryBrand} variant="h3" css={{ margin: "0px 20px" }}>
						{content.theMoreYouBuyHeader}
					</Typography>
					<Typography color={gray} variant="h3" css={{ margin: "0px 20px 20px" }}>
						{content.theMoreYouSaveHeader}
					</Typography>
					<MoreYouBuyMoreYouSaveRows
						variant="light"
						content={content}
						productDetail={productDetail}
						quantityUom={quantityUom}
						priceUom={priceUom}
						filteredDiscountedPrice={filteredDiscountedPrice}
					/>
				</div>
			</Flex>
		</SliderMenu>
	);
};

export default MoreYouBuyMoreYouSaveMobile;
